import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, Mail, Phone, Building2, MapPin, Calendar, DollarSign,
  FileText, Briefcase, MessageSquare, Activity, Clock, ChevronDown, ChevronUp,
  Edit, Save, X, Plus, Download, Eye, ArrowLeft, Tag, TrendingUp, Printer,
  Receipt, CheckSquare, Paperclip, Bell, CreditCard, Ticket, Trash2, Upload,
  AlertCircle, CheckCircle, Circle, MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'profile', label: 'Profile', icon: User },
  { id: 'estimates', label: 'Estimates', icon: FileText },
  { id: 'projects', label: 'Projects', icon: Briefcase },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'payments', label: 'Payments', icon: CreditCard },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'attachments', label: 'Attachments', icon: Paperclip },
  { id: 'reminders', label: 'Reminders', icon: Bell },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'activity', label: 'Activity', icon: Activity },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  pending: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  open: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-500',
};

const CustomerProfilePage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [activities, setActivities] = useState([]);
  const [customerStats, setCustomerStats] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    contact: true,
    address: true,
    subscription: true
  });
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // New state for extras
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [extrasSummary, setExtrasSummary] = useState({});

  // Modal states
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Edit invoice state
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);

  // Form states
  const [invoiceForm, setInvoiceForm] = useState({ title: '', amount: 0, due_date: '', status: 'draft', notes: '' });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', due_date: '', priority: 'medium', status: 'pending' });
  const [reminderForm, setReminderForm] = useState({ title: '', description: '', remind_at: '', reminder_type: 'general' });
  const [paymentForm, setPaymentForm] = useState({ 
    amount: 0, 
    payment_method: 'cash', 
    payment_type: 'deposit',
    reference_number: '', 
    invoice_id: '', 
    project_id: '',
    notes: '' 
  });
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', priority: 'medium', category: 'general' });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');

  // Download PDF for an opportunity
  const handleDownloadPdf = async (opportunityId, title) => {
    setDownloadingPdf(opportunityId);
    try {
      const response = await axios.get(
        `${API_URL}/api/opportunities/${opportunityId}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FusionBuilderPro_Quote_${opportunityId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Quote PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  // Download PDF for a project
  const handleDownloadProjectPdf = async (projectId) => {
    setDownloadingPdf(projectId);
    try {
      const response = await axios.get(
        `${API_URL}/api/projects/${projectId}/pdf`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `FusionBuilderPro_Project_${projectId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Project PDF downloaded!');
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const fetchCustomerData = useCallback(async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [customerRes, activitiesRes, statsRes, oppsRes, projectsRes, extrasRes] = await Promise.all([
        fetch(`${API_URL}/api/customers/${customerId}`, { headers }),
        fetch(`${API_URL}/api/customers/${customerId}/activities`, { headers }),
        fetch(`${API_URL}/api/customers/${customerId}/stats`, { headers }),
        fetch(`${API_URL}/api/opportunities/?customer_id=${customerId}`, { headers }),
        fetch(`${API_URL}/api/projects/?customer_id=${customerId}`, { headers }),
        fetch(`${API_URL}/api/customers/${customerId}/extras-summary`, { headers })
      ]);

      if (customerRes.ok) {
        const data = await customerRes.json();
        setCustomer(data);
        setEditData(data);
      } else {
        toast.error('Customer not found');
        navigate('/crm/customers');
      }
      
      if (activitiesRes.ok) setActivities(await activitiesRes.json());
      if (statsRes.ok) setCustomerStats(await statsRes.json());
      if (oppsRes.ok) setOpportunities(await oppsRes.json());
      if (projectsRes.ok) setProjects(await projectsRes.json());
      if (extrasRes.ok) setExtrasSummary(await extrasRes.json());
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [customerId, token, navigate]);

  // Fetch extras data when tab changes
  const fetchExtrasData = useCallback(async (tab) => {
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      switch (tab) {
        case 'invoices':
          const invRes = await fetch(`${API_URL}/api/customers/${customerId}/invoices`, { headers });
          if (invRes.ok) setInvoices(await invRes.json());
          break;
        case 'tasks':
          const taskRes = await fetch(`${API_URL}/api/customers/${customerId}/tasks`, { headers });
          if (taskRes.ok) setTasks(await taskRes.json());
          break;
        case 'attachments':
          const attRes = await fetch(`${API_URL}/api/customers/${customerId}/attachments`, { headers });
          if (attRes.ok) setAttachments(await attRes.json());
          break;
        case 'reminders':
          const remRes = await fetch(`${API_URL}/api/customers/${customerId}/reminders`, { headers });
          if (remRes.ok) setReminders(await remRes.json());
          break;
        case 'payments':
          const payRes = await fetch(`${API_URL}/api/customers/${customerId}/payments`, { headers });
          if (payRes.ok) setPayments(await payRes.json());
          break;
        case 'tickets':
          const tickRes = await fetch(`${API_URL}/api/customers/${customerId}/tickets`, { headers });
          if (tickRes.ok) setTickets(await tickRes.json());
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${tab}:`, error);
    }
  }, [customerId, token]);

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
    }
  }, [customerId, fetchCustomerData]);

  useEffect(() => {
    if (['invoices', 'tasks', 'attachments', 'reminders', 'payments', 'tickets'].includes(activeTab)) {
      fetchExtrasData(activeTab);
    }
  }, [activeTab, fetchExtrasData]);

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updated = await response.json();
        setCustomer(updated);
        setIsEditing(false);
        toast.success('Customer updated successfully');
        fetchCustomerData();
      } else {
        toast.error('Failed to update customer');
      }
    } catch (error) {
      toast.error('Failed to update customer');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'note',
          title: 'Note Added',
          description: newNote
        })
      });

      if (response.ok) {
        toast.success('Note added');
        setNewNote('');
        setShowAddNoteModal(false);
        fetchCustomerData();
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  // CRUD handlers for extras
  const handleCreateInvoice = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceForm)
      });
      if (response.ok) {
        toast.success('Invoice created');
        setShowInvoiceModal(false);
        setInvoiceForm({ title: '', amount: 0, due_date: '', status: 'draft', notes: '' });
        fetchExtrasData('invoices');
        fetchCustomerData();
      } else {
        toast.error('Failed to create invoice');
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice({
      ...invoice,
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : ''
    });
    setShowEditInvoiceModal(true);
  };

  const handleUpdateInvoice = async () => {
    if (!editingInvoice) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/invoices/${editingInvoice.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingInvoice.title,
          amount: editingInvoice.amount,
          due_date: editingInvoice.due_date,
          status: editingInvoice.status,
          notes: editingInvoice.notes
        })
      });
      if (response.ok) {
        toast.success('Invoice updated');
        setShowEditInvoiceModal(false);
        setEditingInvoice(null);
        fetchExtrasData('invoices');
        fetchCustomerData();
      } else {
        toast.error('Failed to update invoice');
      }
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleConvertInvoiceToEstimate = async (invoice) => {
    if (!window.confirm('Convert this invoice back to an estimate? The invoice will be deleted.')) return;
    
    try {
      // Create opportunity from invoice data
      const opportunityData = {
        title: invoice.title.replace('Invoice for ', ''),
        description: `Converted from invoice ${invoice.invoice_number}`,
        value: invoice.amount,
        stage: 'qualified',
        customer_id: customerId
      };

      const createResponse = await fetch(`${API_URL}/api/opportunities/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(opportunityData)
      });

      if (createResponse.ok) {
        // Delete the invoice
        await fetch(`${API_URL}/api/customers/${customerId}/invoices/${invoice.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        toast.success('Invoice converted back to estimate!');
        fetchExtrasData('invoices');
        fetchCustomerData();
        // Switch to estimates tab
        setActiveTab('estimates');
      } else {
        toast.error('Failed to create estimate');
      }
    } catch (error) {
      console.error('Error converting invoice to estimate:', error);
      toast.error('Failed to convert invoice to estimate');
    }
  };

  const handleCreateTask = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/tasks`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(taskForm)
      });
      if (response.ok) {
        toast.success('Task created');
        setShowTaskModal(false);
        setTaskForm({ title: '', description: '', due_date: '', priority: 'medium', status: 'pending' });
        fetchExtrasData('tasks');
        fetchCustomerData();
      } else {
        toast.error('Failed to create task');
      }
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/customers/${customerId}/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchExtrasData('tasks');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleCreateReminder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/reminders`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(reminderForm)
      });
      if (response.ok) {
        toast.success('Reminder created');
        setShowReminderModal(false);
        setReminderForm({ title: '', description: '', remind_at: '', reminder_type: 'general' });
        fetchExtrasData('reminders');
        fetchCustomerData();
      } else {
        toast.error('Failed to create reminder');
      }
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteReminder = async (reminderId) => {
    try {
      await fetch(`${API_URL}/api/customers/${customerId}/reminders/${reminderId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_completed: true })
      });
      fetchExtrasData('reminders');
      toast.success('Reminder completed');
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const handleCreatePayment = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/payments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentForm)
      });
      if (response.ok) {
        toast.success('Payment recorded');
        setShowPaymentModal(false);
        setPaymentForm({ 
          amount: 0, 
          payment_method: 'cash', 
          payment_type: 'deposit',
          reference_number: '', 
          invoice_id: '', 
          project_id: '',
          notes: '' 
        });
        fetchExtrasData('payments');
        fetchExtrasData('invoices');
        fetchCustomerData();
      } else {
        toast.error('Failed to record payment');
      }
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const handleCreateTicket = async () => {
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/tickets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(ticketForm)
      });
      if (response.ok) {
        toast.success('Ticket created');
        setShowTicketModal(false);
        setTicketForm({ subject: '', description: '', priority: 'medium', category: 'general' });
        fetchExtrasData('tickets');
        fetchCustomerData();
      } else {
        toast.error('Failed to create ticket');
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const handleUpdateTicketStatus = async (ticketId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/customers/${customerId}/tickets/${ticketId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      fetchExtrasData('tickets');
      toast.success('Ticket updated');
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const handleUploadAttachment = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('description', uploadDescription);

    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/attachments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        toast.success('File uploaded');
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadDescription('');
        fetchExtrasData('attachments');
        fetchCustomerData();
      } else {
        toast.error('Failed to upload file');
      }
    } catch (error) {
      toast.error('Failed to upload file');
    }
  };

  const handleConvertToInvoice = async (opportunity) => {
    try {
      // Create invoice from opportunity data
      const invoiceData = {
        title: `Invoice for ${opportunity.title}`,
        amount: opportunity.value || 0,
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
        status: 'draft',
        notes: `Converted from estimate: ${opportunity.title}\n${opportunity.description || ''}\nOriginal Estimate ID: ${opportunity.id}`
      };

      const response = await fetch(`${API_URL}/api/customers/${customerId}/invoices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceData)
      });

      if (response.ok) {
        const newInvoice = await response.json();
        
        // Delete the opportunity/estimate after successful invoice creation
        const deleteResponse = await fetch(`${API_URL}/api/opportunities/${opportunity.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (deleteResponse.ok) {
          // Remove from local state
          setOpportunities(prev => prev.filter(o => o.id !== opportunity.id));
          toast.success(`Invoice ${newInvoice.invoice_number} created and estimate removed!`);
        } else {
          toast.success(`Invoice ${newInvoice.invoice_number} created! (Note: Could not remove estimate)`);
        }
        
        fetchExtrasData('invoices');
        fetchCustomerData();
        // Switch to invoices tab to show the new invoice
        setActiveTab('invoices');
      } else {
        toast.error('Failed to create invoice from estimate');
      }
    } catch (error) {
      console.error('Error converting to invoice:', error);
      toast.error('Failed to convert estimate to invoice');
    }
  };

  const handleDeleteItem = async (type, itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}/${type}/${itemId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Item deleted');
        fetchExtrasData(type);
        fetchCustomerData();
      } else {
        toast.error('Failed to delete item');
      }
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!customer) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Customer not found</p>
          <Button onClick={() => navigate('/crm/customers')} className="mt-4">
            Back to Customers
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/crm/customers')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                {customer.first_name?.[0]}{customer.last_name?.[0]}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customer.first_name} {customer.last_name}
                </h1>
                <p className="text-gray-500 dark:text-gray-400">{customer.email}</p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => { setIsEditing(false); setEditData(customer); }}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} data-testid="save-profile-btn">
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          <nav className="flex gap-4 min-w-max">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'estimates' && opportunities.length > 0 && (
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
                    {opportunities.length}
                  </span>
                )}
                {tab.id === 'projects' && projects.length > 0 && (
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
                    {projects.length}
                  </span>
                )}
                {tab.id === 'invoices' && extrasSummary.invoices > 0 && (
                  <span className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs px-2 py-0.5 rounded-full">
                    {extrasSummary.invoices}
                  </span>
                )}
                {tab.id === 'tasks' && extrasSummary.pending_tasks > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs px-2 py-0.5 rounded-full">
                    {extrasSummary.pending_tasks}
                  </span>
                )}
                {tab.id === 'tickets' && extrasSummary.open_tickets > 0 && (
                  <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                    {extrasSummary.open_tickets}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left/Center */}
          <div className="lg:col-span-2 space-y-6">
            {activeTab === 'profile' && (
              <>
                {/* Contact Information */}
                <Card>
                  <CardHeader 
                    className="cursor-pointer flex flex-row items-center justify-between"
                    onClick={() => toggleSection('contact')}
                  >
                    <CardTitle className="text-base flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Contact Information
                    </CardTitle>
                    {expandedSections.contact ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CardHeader>
                  {expandedSections.contact && (
                    <CardContent className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</label>
                        {isEditing ? (
                          <Input
                            value={editData.email || ''}
                            onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="font-medium flex items-center gap-2 mt-1 text-gray-900 dark:text-white">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {customer.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Phone</label>
                        {isEditing ? (
                          <Input
                            value={editData.phone || ''}
                            onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="font-medium flex items-center gap-2 mt-1 text-gray-900 dark:text-white">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {customer.phone || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Company</label>
                        {isEditing ? (
                          <Input
                            value={editData.company || ''}
                            onChange={(e) => setEditData({ ...editData, company: e.target.value })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="font-medium flex items-center gap-2 mt-1 text-gray-900 dark:text-white">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            {customer.company || <span className="text-gray-400 italic">Not provided</span>}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Roles</label>
                        <div className="flex gap-2 mt-1">
                          {customer.roles?.map((role, i) => (
                            <span key={i} className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-1 rounded-full">
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Address */}
                <Card>
                  <CardHeader 
                    className="cursor-pointer flex flex-row items-center justify-between"
                    onClick={() => toggleSection('address')}
                  >
                    <CardTitle className="text-base flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Address
                    </CardTitle>
                    {expandedSections.address ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CardHeader>
                  {expandedSections.address && (
                    <CardContent>
                      {isEditing ? (
                        <div className="space-y-3">
                          <Input
                            placeholder="Street Address"
                            value={editData.address?.street || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              address: { ...editData.address, street: e.target.value }
                            })}
                          />
                          <div className="grid grid-cols-3 gap-3">
                            <Input
                              placeholder="City"
                              value={editData.address?.city || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                address: { ...editData.address, city: e.target.value }
                              })}
                            />
                            <Input
                              placeholder="State"
                              value={editData.address?.state || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                address: { ...editData.address, state: e.target.value }
                              })}
                            />
                            <Input
                              placeholder="ZIP Code"
                              value={editData.address?.zip_code || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                address: { ...editData.address, zip_code: e.target.value }
                              })}
                            />
                          </div>
                        </div>
                      ) : customer.address?.street ? (
                        <div className="text-gray-900 dark:text-white">
                          <p className="font-medium">{customer.address.street}</p>
                          <p className="text-gray-600 dark:text-gray-300">
                            {customer.address.city}, {customer.address.state} {customer.address.zip_code}
                          </p>
                          <p className="text-gray-500 dark:text-gray-400">{customer.address.country}</p>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">Not provided</p>
                      )}
                    </CardContent>
                  )}
                </Card>

                {/* Subscription */}
                <Card>
                  <CardHeader 
                    className="cursor-pointer flex flex-row items-center justify-between"
                    onClick={() => toggleSection('subscription')}
                  >
                    <CardTitle className="text-base flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Subscription
                    </CardTitle>
                    {expandedSections.subscription ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </CardHeader>
                  {expandedSections.subscription && (
                    <CardContent>
                      {isEditing ? (
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Plan Name</label>
                            <Input
                              value={editData.subscription?.plan_name || ''}
                              onChange={(e) => setEditData({
                                ...editData,
                                subscription: { ...editData.subscription, plan_name: e.target.value }
                              })}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                            <select
                              className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              value={editData.subscription?.status || 'inactive'}
                              onChange={(e) => setEditData({
                                ...editData,
                                subscription: { ...editData.subscription, status: e.target.value }
                              })}
                            >
                              <option value="inactive">Inactive</option>
                              <option value="active">Active</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Monthly Value</label>
                            <Input
                              type="number"
                              value={editData.subscription?.monthly_value || 0}
                              onChange={(e) => setEditData({
                                ...editData,
                                subscription: { ...editData.subscription, monthly_value: parseFloat(e.target.value) || 0 }
                              })}
                              className="mt-1"
                            />
                          </div>
                        </div>
                      ) : customer.subscription?.status === 'active' ? (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-gray-900 dark:text-white">{customer.subscription.plan_name || 'Active Plan'}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              active
                            </span>
                            {customer.subscription.started_at && (
                              <p className="text-sm text-gray-500 mt-1">
                                Started: {new Date(customer.subscription.started_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">${customer.subscription.monthly_value || 0}</p>
                            <p className="text-gray-500 dark:text-gray-400">/month</p>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic">No active subscription</p>
                      )}
                    </CardContent>
                  )}
                </Card>

                {/* Tags */}
                {customer.tags?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Tag className="w-4 h-4" />
                        Tags
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {customer.tags.map((tag, i) => (
                          <span key={i} className="bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full text-sm text-gray-700 dark:text-gray-300">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {activeTab === 'estimates' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Estimates & Quotes</CardTitle>
                  <Link to="/">
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      New Estimate
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {opportunities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No estimates yet</p>
                      <p className="text-sm">Create a new estimate from the configurator</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {opportunities.map((opp) => (
                        <div key={opp.id} className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{opp.title}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">{opp.description}</p>
                              <div className="flex items-center gap-4 mt-2 text-sm">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  opp.stage === 'won' ? 'bg-green-100 text-green-800' :
                                  opp.stage === 'lost' ? 'bg-red-100 text-red-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {opp.stage}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  {new Date(opp.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-green-600">
                                ${opp.value?.toLocaleString()}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-2 justify-end">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleDownloadPdf(opp.id, opp.title)}
                                  disabled={downloadingPdf === opp.id}
                                  data-testid={`download-pdf-${opp.id}`}
                                >
                                  <Printer className={`w-4 h-4 mr-1 ${downloadingPdf === opp.id ? 'animate-pulse' : ''}`} />
                                  {downloadingPdf === opp.id ? '...' : 'PDF'}
                                </Button>
                                <Link to={`/?edit=${opp.id}`}>
                                  <Button variant="outline" size="sm" title="Edit Quote in Configurator">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Button 
                                  variant="default" 
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleConvertToInvoice(opp)}
                                  title="Convert this estimate to an invoice"
                                  data-testid={`convert-to-invoice-${opp.id}`}
                                >
                                  <Receipt className="w-4 h-4 mr-1" />
                                  Invoice
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'projects' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Projects</CardTitle>
                  <Link to="/crm/projects">
                    <Button size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      New Project
                    </Button>
                  </Link>
                </CardHeader>
                <CardContent>
                  {projects.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No projects yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {projects.map((project) => (
                        <div key={project.id} className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{project.name}</p>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-800'}`}>
                                {project.status?.replace('_', ' ')}
                              </span>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-gray-900 dark:text-white">${project.total_value?.toLocaleString()}</p>
                              {project.configuration && (
                                <Button variant="outline" size="sm" onClick={() => handleDownloadProjectPdf(project.id)} disabled={downloadingPdf === project.id}>
                                  <Printer className="w-4 h-4 mr-1" />
                                  PDF
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'invoices' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Invoices</CardTitle>
                  <Button size="sm" onClick={() => setShowInvoiceModal(true)} data-testid="add-invoice-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Invoice
                  </Button>
                </CardHeader>
                <CardContent>
                  {invoices.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No invoices yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-medium text-gray-900 dark:text-white">{invoice.title}</p>
                              <p className="text-sm text-gray-500">{invoice.invoice_number}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[invoice.status] || 'bg-gray-100 text-gray-800'}`}>
                                  {invoice.status}
                                </span>
                                {invoice.due_date && (
                                  <span className="text-xs text-gray-500">Due: {new Date(invoice.due_date).toLocaleDateString()}</span>
                                )}
                                {invoice.notes?.includes('Converted from estimate') && (
                                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                    From Estimate
                                  </span>
                                )}
                              </div>
                              {invoice.notes && (
                                <p className="text-xs text-gray-400 mt-2 italic line-clamp-2">{invoice.notes}</p>
                              )}
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-xl font-bold text-gray-900 dark:text-white">${invoice.amount?.toLocaleString()}</p>
                              <div className="flex items-center gap-1 mt-2 justify-end flex-wrap">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleEditInvoice(invoice)}
                                  title="Edit invoice"
                                  data-testid={`edit-invoice-${invoice.id}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {invoice.notes?.includes('Converted from estimate') && (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    className="text-purple-600 border-purple-200 hover:bg-purple-50"
                                    onClick={() => handleConvertInvoiceToEstimate(invoice)}
                                    title="Convert back to estimate"
                                    data-testid={`convert-to-estimate-${invoice.id}`}
                                  >
                                    <FileText className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => handleDeleteItem('invoices', invoice.id)}
                                  title="Delete invoice"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'payments' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Payments</CardTitle>
                  <Button size="sm" onClick={() => setShowPaymentModal(true)} data-testid="add-payment-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No payments recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {payments.map((payment) => (
                        <div key={payment.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-medium text-gray-900 dark:text-white">{payment.payment_number}</p>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  payment.payment_type === 'deposit' ? 'bg-blue-100 text-blue-700' :
                                  payment.payment_type === 'final_payment' ? 'bg-green-100 text-green-700' :
                                  payment.payment_type === 'partial_payment' ? 'bg-yellow-100 text-yellow-700' :
                                  payment.payment_type === 'refund' ? 'bg-red-100 text-red-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {payment.payment_type?.replace('_', ' ') || 'Payment'}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                                <span className="capitalize">{payment.payment_method?.replace('_', ' ')}</span>
                                {payment.reference_number && (
                                  <span>Ref: {payment.reference_number}</span>
                                )}
                                {payment.invoice_id && invoices.find(i => i.id === payment.invoice_id) && (
                                  <span className="text-blue-600">
                                    Invoice: {invoices.find(i => i.id === payment.invoice_id)?.invoice_number}
                                  </span>
                                )}
                                {payment.project_id && projects.find(p => p.id === payment.project_id) && (
                                  <span className="text-purple-600">
                                    Project: {projects.find(p => p.id === payment.project_id)?.name}
                                  </span>
                                )}
                              </div>
                              {payment.notes && (
                                <p className="text-sm text-gray-600 dark:text-gray-300 mt-2 italic">"{payment.notes}"</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(payment.created_at).toLocaleString()} by {payment.recorded_by_name || 'Unknown'}
                              </p>
                            </div>
                            <div className="text-right flex items-center gap-3 ml-4">
                              <p className={`text-xl font-bold ${payment.payment_type === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                                {payment.payment_type === 'refund' ? '-' : ''}${payment.amount?.toLocaleString()}
                              </p>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem('payments', payment.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'tasks' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Tasks</CardTitle>
                  <Button size="sm" onClick={() => setShowTaskModal(true)} data-testid="add-task-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Task
                  </Button>
                </CardHeader>
                <CardContent>
                  {tasks.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No tasks yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tasks.map((task) => (
                        <div key={task.id} className={`border dark:border-gray-700 rounded-lg p-4 ${task.status === 'completed' ? 'opacity-60' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <button
                                onClick={() => handleUpdateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                                className="mt-1"
                              >
                                {task.status === 'completed' ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <Circle className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                                )}
                              </button>
                              <div>
                                <p className={`font-medium text-gray-900 dark:text-white ${task.status === 'completed' ? 'line-through' : ''}`}>{task.title}</p>
                                {task.description && <p className="text-sm text-gray-500">{task.description}</p>}
                                <div className="flex items-center gap-2 mt-2">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[task.priority]}`}>
                                    {task.priority}
                                  </span>
                                  {task.due_date && (
                                    <span className="text-xs text-gray-500">Due: {new Date(task.due_date).toLocaleDateString()}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem('tasks', task.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'attachments' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Attachments</CardTitle>
                  <Button size="sm" onClick={() => setShowUploadModal(true)} data-testid="add-attachment-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </Button>
                </CardHeader>
                <CardContent>
                  {attachments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Paperclip className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No attachments yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {attachments.map((attachment) => (
                        <div key={attachment.id} className="border dark:border-gray-700 rounded-lg p-4 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center">
                              <Paperclip className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{attachment.filename}</p>
                              <p className="text-xs text-gray-500">{formatFileSize(attachment.file_size)} • {new Date(attachment.created_at).toLocaleDateString()}</p>
                              {attachment.description && <p className="text-sm text-gray-500">{attachment.description}</p>}
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteItem('attachments', attachment.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'reminders' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Reminders</CardTitle>
                  <Button size="sm" onClick={() => setShowReminderModal(true)} data-testid="add-reminder-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Reminder
                  </Button>
                </CardHeader>
                <CardContent>
                  {reminders.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No reminders set</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reminders.map((reminder) => (
                        <div key={reminder.id} className={`border dark:border-gray-700 rounded-lg p-4 ${reminder.is_completed ? 'opacity-60' : ''}`}>
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <button onClick={() => !reminder.is_completed && handleCompleteReminder(reminder.id)} className="mt-1">
                                {reminder.is_completed ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <Bell className="w-5 h-5 text-orange-500" />
                                )}
                              </button>
                              <div>
                                <p className={`font-medium text-gray-900 dark:text-white ${reminder.is_completed ? 'line-through' : ''}`}>{reminder.title}</p>
                                {reminder.description && <p className="text-sm text-gray-500">{reminder.description}</p>}
                                <p className="text-xs text-orange-600 mt-1">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {new Date(reminder.remind_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteItem('reminders', reminder.id)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'tickets' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Support Tickets</CardTitle>
                  <Button size="sm" onClick={() => setShowTicketModal(true)} data-testid="add-ticket-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    New Ticket
                  </Button>
                </CardHeader>
                <CardContent>
                  {tickets.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Ticket className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No tickets yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {tickets.map((ticket) => (
                        <div key={ticket.id} className="border dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{ticket.subject}</p>
                              <p className="text-sm text-gray-500">{ticket.ticket_number}</p>
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'}`}>
                                  {ticket.status}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
                                  {ticket.priority}
                                </span>
                                <span className="text-xs text-gray-500 capitalize">{ticket.category}</span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {ticket.status === 'open' && (
                                <Button variant="outline" size="sm" onClick={() => handleUpdateTicketStatus(ticket.id, 'resolved')}>
                                  Resolve
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteItem('tickets', ticket.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'notes' && (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-base">Notes</CardTitle>
                  <Button size="sm" onClick={() => setShowAddNoteModal(true)} data-testid="add-note-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Note
                  </Button>
                </CardHeader>
                <CardContent>
                  {activities.filter(a => a.type === 'note').length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No notes yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activities.filter(a => a.type === 'note').map((note) => (
                        <div key={note.id} className="border-l-4 border-blue-400 pl-4 py-2">
                          <p className="text-gray-800 dark:text-gray-100">{note.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
                            <span>{note.user_name || 'System'}</span>
                            <span>{new Date(note.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {activeTab === 'activity' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Activity Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  {activities.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No activity yet</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {activities.map((activity, index) => (
                        <div key={activity.id} className="flex gap-4">
                          <div className="relative">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              activity.type === 'created' ? 'bg-green-100' :
                              activity.type === 'status_change' ? 'bg-blue-100' :
                              activity.type === 'note' ? 'bg-yellow-100' :
                              'bg-gray-100'
                            }`}>
                              <Activity className={`w-4 h-4 ${
                                activity.type === 'created' ? 'text-green-600' :
                                activity.type === 'status_change' ? 'text-blue-600' :
                                activity.type === 'note' ? 'text-yellow-600' :
                                'text-gray-600'
                              }`} />
                            </div>
                            {index < activities.length - 1 && (
                              <div className="absolute top-10 left-1/2 w-0.5 h-full -translate-x-1/2 bg-gray-200 dark:bg-gray-700"></div>
                            )}
                          </div>
                          <div className="flex-1 pb-4">
                            <p className="font-medium text-gray-900 dark:text-white">{activity.title}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-300">{activity.description}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                              <span>{activity.user_name || 'System'}</span>
                              <span>{new Date(activity.created_at).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Right */}
          <div className="space-y-6">
            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Total Invoiced</span>
                  <span className="font-bold text-gray-900 dark:text-white">${extrasSummary.total_invoiced?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Total Paid</span>
                  <span className="font-bold text-green-600">${extrasSummary.total_paid?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Pending Tasks</span>
                  <span className="font-bold text-orange-600">{extrasSummary.pending_tasks || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Open Tickets</span>
                  <span className="font-bold text-blue-600">{extrasSummary.open_tickets || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Reminders</span>
                  <span className="font-bold text-gray-900 dark:text-white">{extrasSummary.reminders || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-300">Attachments</span>
                  <span className="font-bold text-gray-900 dark:text-white">{extrasSummary.attachments || 0}</span>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activities.slice(0, 5).length === 0 ? (
                  <p className="text-gray-400 text-sm text-center py-4">No activity yet</p>
                ) : (
                  <div className="space-y-3">
                    {activities.slice(0, 5).map((activity) => (
                      <div key={activity.id} className="flex gap-3 text-sm">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{activity.title}</p>
                          <p className="text-xs text-gray-400">
                            {new Date(activity.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Note Modal */}
      <Dialog open={showAddNoteModal} onOpenChange={setShowAddNoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <textarea
              className="w-full border rounded-md px-3 py-2 min-h-[120px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="Enter your note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              data-testid="note-textarea"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNoteModal(false)}>Cancel</Button>
            <Button onClick={handleAddNote} data-testid="save-note-btn">Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={invoiceForm.title} onChange={(e) => setInvoiceForm({ ...invoiceForm, title: e.target.value })} placeholder="Invoice title" />
            </div>
            <div>
              <label className="text-sm font-medium">Amount</label>
              <Input type="number" value={invoiceForm.amount} onChange={(e) => setInvoiceForm({ ...invoiceForm, amount: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Status</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={invoiceForm.status} onChange={(e) => setInvoiceForm({ ...invoiceForm, status: e.target.value })}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-200">Notes</label>
              <textarea className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={invoiceForm.notes} onChange={(e) => setInvoiceForm({ ...invoiceForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceModal(false)}>Cancel</Button>
            <Button onClick={handleCreateInvoice}>Create Invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal */}
      <Dialog open={showEditInvoiceModal} onOpenChange={setShowEditInvoiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Invoice
            </DialogTitle>
          </DialogHeader>
          {editingInvoice && (
            <div className="space-y-4 py-4">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-2">
                <p className="text-sm text-gray-500">Invoice Number</p>
                <p className="font-medium">{editingInvoice.invoice_number}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
                <Input 
                  value={editingInvoice.title} 
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, title: e.target.value })} 
                  placeholder="Invoice title" 
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Amount</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input 
                    type="number" 
                    className="pl-7"
                    value={editingInvoice.amount} 
                    onChange={(e) => setEditingInvoice({ ...editingInvoice, amount: parseFloat(e.target.value) || 0 })} 
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Due Date</label>
                <Input 
                  type="date" 
                  className="mt-1"
                  value={editingInvoice.due_date} 
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, due_date: e.target.value })} 
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                <select 
                  className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                  value={editingInvoice.status} 
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, status: e.target.value })}
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
                <textarea 
                  className="w-full border rounded-md px-3 py-2 mt-1 min-h-[80px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                  value={editingInvoice.notes || ''} 
                  onChange={(e) => setEditingInvoice({ ...editingInvoice, notes: e.target.value })} 
                  placeholder="Additional notes..."
                />
              </div>
              {editingInvoice.notes?.includes('Converted from estimate') && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                  <p className="text-sm text-purple-700 dark:text-purple-300 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    This invoice was converted from an estimate. You can convert it back using the button on the invoice card.
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditInvoiceModal(false); setEditingInvoice(null); }}>
              Cancel
            </Button>
            <Button onClick={handleUpdateInvoice} data-testid="save-invoice-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Task title" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTask}>Create Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={showReminderModal} onOpenChange={setShowReminderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={reminderForm.title} onChange={(e) => setReminderForm({ ...reminderForm, title: e.target.value })} placeholder="Reminder title" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={reminderForm.description} onChange={(e) => setReminderForm({ ...reminderForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Remind At</label>
              <Input type="datetime-local" value={reminderForm.remind_at} onChange={(e) => setReminderForm({ ...reminderForm, remind_at: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600" value={reminderForm.reminder_type} onChange={(e) => setReminderForm({ ...reminderForm, reminder_type: e.target.value })}>
                <option value="general">General</option>
                <option value="follow_up">Follow Up</option>
                <option value="payment">Payment</option>
                <option value="meeting">Meeting</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReminderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateReminder}>Create Reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Amount *</label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input 
                    type="number" 
                    className="pl-7"
                    value={paymentForm.amount} 
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: parseFloat(e.target.value) || 0 })} 
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Type *</label>
                <select 
                  className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                  value={paymentForm.payment_type} 
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_type: e.target.value })}
                >
                  <option value="deposit">Deposit</option>
                  <option value="partial_payment">Partial Payment</option>
                  <option value="final_payment">Final Payment</option>
                  <option value="refund">Refund</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Payment Method</label>
                <select 
                  className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                  value={paymentForm.payment_method} 
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                >
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="ach">ACH</option>
                  <option value="wire">Wire Transfer</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Reference Number</label>
                <Input 
                  className="mt-1"
                  value={paymentForm.reference_number} 
                  onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })} 
                  placeholder="Check #, Trans ID, etc." 
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Invoice #</label>
              <select 
                className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                value={paymentForm.invoice_id} 
                onChange={(e) => setPaymentForm({ ...paymentForm, invoice_id: e.target.value })}
              >
                <option value="">-- Select Invoice (Optional) --</option>
                {invoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoice_number} - {inv.title} (${inv.amount?.toLocaleString()}) {inv.status === 'paid' ? '✓ Paid' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Project Name</label>
              <select 
                className="w-full border rounded-md px-3 py-2 mt-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                value={paymentForm.project_id} 
                onChange={(e) => setPaymentForm({ ...paymentForm, project_id: e.target.value })}
              >
                <option value="">-- Select Project (Optional) --</option>
                {projects.map(proj => (
                  <option key={proj.id} value={proj.id}>
                    {proj.name} (${proj.total_value?.toLocaleString()})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes on Payment</label>
              <textarea 
                className="w-full border rounded-md px-3 py-2 mt-1 min-h-[80px] bg-white dark:bg-gray-800 text-gray-900 dark:text-white" 
                value={paymentForm.notes} 
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} 
                placeholder="Add any notes about this payment..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentModal(false)}>Cancel</Button>
            <Button onClick={handleCreatePayment} disabled={!paymentForm.amount}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Subject</label>
              <Input value={ticketForm.subject} onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })} placeholder="Ticket subject" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <textarea className="w-full border rounded-md px-3 py-2 min-h-[100px] bg-white dark:bg-gray-800" value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Priority</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800" value={ticketForm.priority} onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Category</label>
              <select className="w-full border rounded-md px-3 py-2 bg-white dark:bg-gray-800" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
                <option value="general">General</option>
                <option value="billing">Billing</option>
                <option value="technical">Technical</option>
                <option value="complaint">Complaint</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketModal(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket}>Create Ticket</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Attachment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">File</label>
              <Input type="file" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </div>
            <div>
              <label className="text-sm font-medium">Description (Optional)</label>
              <Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Brief description of the file" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUploadAttachment}>Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CustomerProfilePage;
